import { ApplicationElement, html } from '../application_element/element.js';

import translations from './translations.js';
import style from './style.lit.css';

import '../dropdown/element.js';
import '../chat_list/element.js';
import '../icon_block/element.js';
import { StoreController } from '../../lib/lit/store_controller.js';
import { computedChatsStore, markAllAsRead } from '../chat/lib/chats.store.js';

/**
 * Navbar chat list dropdown - on large screens acts as a dropdown with a list of latest chats, on small screens as a link to chats page
 *
 * @prop markAllAsReadUrl - API endpoint URL for marking all chats as read (POST request will be sent)
 * @prop loadChatsUrl - API endpoint URL for fetching the chats
 * @prop chatsPageUrl - URL of applications page that displays all chats
 * @prop open - Is the dropdown open or closed? Will be controlled internally, but it can also be passed a default value (current default = false - closed)
 * @prop muted - Show muted or unmuted chats? Will be changed internally, but it can also be passed a default value (current default = false - unmuted chats)
 *
 *
 * @slot create - Button for creating a new chat
 */

export default class ChatTopDropdown extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  static properties = {
    markAllAsReadUrl: { type: String },
    chatsPageUrl: { type: String },
    loadChatsUrl: { type: String },
    muted: { type: Boolean },
    open: { type: Boolean },
    icon: { type: String },
    onlyIcon: { type: Boolean },
  };

  /** @internal */
  static translations = translations;

  storeController = new StoreController(this, computedChatsStore);

  constructor() {
    super();

    this.open = false;
    this.muted = false;
    this.icon = 'comment-alt-lines-s';
    this.onlyIcon = false;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this);
  }

  handleEvent(e) {
    e.type === 'resize' && this.requestUpdate();
  }

  get store() {
    return this.storeController.value;
  }

  get unreadCounters() {
    return this.store.unreadCounters;
  }

  get mutedCount() {
    return this.unreadCounters.muted;
  }

  get allCount() {
    return this.unreadCounters.allWithoutMuted;
  }

  /** @internal */
  get isSmallDevice() {
    return window.innerWidth < 768;
  }

  /** @internal */
  get hasUnread() {
    return this.unreadCounters.all;
  }

  get chatList() {
    return this.shadowRoot.querySelector('ef-chat-list');
  }

  get showMutedNote() {
    return this.unreadCounters.muted && !this.muted;
  }

  get hasUnsentMessages() {
    return this.store.hasUnsentMessages;
  }

  toggleMuted() {
    return (this.muted = !this.muted);
  }

  handleMarkAllAsRead() {
    return markAllAsRead(this.markAllAsReadUrl);
  }

  renderIcon() {
    return html`
      <ef-icon name=${this.icon}></ef-icon>
      <ef-badge count=${this.allCount}></ef-badge>
    `;
  }

  renderUnreadMutedNote() {
    return html`
      <button class="toggle-muted-btn btn-link" @click=${this.toggleMuted}>
        <ef-icon-block
          icon="minus-circle"
          text=${this.t('unread_muted_note', {
            count: this.mutedCount,
          })}>
        </ef-icon-block>
      </button>
    `;
  }

  renderBack() {
    return html`
      <button class="toggle-muted-btn btn-link" @click=${this.toggleMuted}>
        <ef-icon-block icon="chevron-left" text=${this.t('back')}>
        </ef-icon-block>
      </button>
    `;
  }

  renderMarkAllAsRead() {
    return html`
      <slot name="header-left" @click=${this.handleMarkAllAsRead}>
        <button class="btn-link">
          <ef-icon-block
            icon="check-double"
            text="${this.t('mark_all_as_read')}">
          </ef-icon-block>
        </button>
      </slot>
    `;
  }

  renderFooter() {
    return html`
      <footer>
        <slot name="footer">
          <a class="btn-link" href=${this.chatsPageUrl}>
            <ef-icon-block icon=${this.icon} text="${this.t('show_all')}">
            </ef-icon-block>
          </a>
        </slot>
      </footer>
    `;
  }

  renderFull() {
    return html`
      <ef-dropdown open=${this.open} placement="bottom-end">
        <button slot="trigger" class="icon-btn">${this.renderIcon()}</button>
        <div class="dropdown">
          <header>
            ${this.hasUnread ? this.renderMarkAllAsRead() : null}
            <slot name="header-right">
              <button class="btn-link">
                <ef-icon-block icon="plus" text=${this.t('new_chat')}>
                </ef-icon-block>
              </button>
            </slot>
          </header>

          <main>
            ${this.showMutedNote ? this.renderUnreadMutedNote() : null}
            ${this.muted ? this.renderBack() : null}
            <ef-chat-list
              exportParts="ef-chat-list-item"
              loadChatsUrl=${this.loadChatsUrl}
              ?muted=${this.muted}>
              <slot name="empty" slot="empty"></slot>
            </ef-chat-list>
          </main>

          ${this.store.counters.all ? this.renderFooter() : null}
        </div>
      </ef-dropdown>
    `;
  }

  // We need to render list to get unread counters
  renderOnlyIcon() {
    return html`
      <a slot="trigger" href=${this.chatsPageUrl} class="icon-btn">
        ${this.renderIcon()}
      </a>
      <ef-chat-list hidden loadChatsUrl=${this.loadChatsUrl}></ef-chat-list>
    `;
  }

  render() {
    return this.onlyIcon || this.isSmallDevice
      ? this.renderOnlyIcon()
      : this.renderFull();
  }
}

ChatTopDropdown.registerTagName('ef-chat-top-dropdown');

customElements.get('ef-chat-top-dropdown') ||
  customElements.define('ef-chat-top-dropdown', ChatTopDropdown);
